import { useEffect, useId, useMemo, useRef, useState, type Dispatch, type SetStateAction } from 'react';
import { DateField } from '@/components/DateField';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useEventCallback } from '../../../ui/hooks';
import { isNull, isObject } from '../../../ui/utils';
import type { FilterControlProps, FilterField, SetQuery } from '../types';
import { FilterGroup } from './SelectControls';

type Range = [number | null, number | null];
type ControlProps = Record<string, unknown> & { min?: number; max?: number; from?: number; to?: number };
type RangeTest = { name: string; test: (val: unknown, props?: ControlProps) => boolean };
type ErrorStrings = Record<string, string | ((value: unknown, props: ControlProps) => string)>;
type InputError = { text?: string } | null;

export const rangeTests: RangeTest[] = [
  { name: 'number', test: (val) => isNull(val) || (typeof val === 'number' && !isNaN(val)) },
  { name: 'gteMin', test: (val, props = {}) => isNull(val) || isNull(props.min) || (val as number) >= (props.min as number) },
  { name: 'lteMax', test: (val, props = {}) => isNull(val) || isNull(props.max) || (val as number) <= (props.max as number) },
];

/** A range filter's two values, their validation, and submitting the range to the query. */
export const useRangeControl = (
  valueProp: unknown,
  field: FilterField,
  setQuery: SetQuery,
  minKey = 'min',
  maxKey = 'max',
  tests: RangeTest[],
  errorStrings: ErrorStrings
): [
  Range,
  Dispatch<SetStateAction<Range>>,
  Record<string, ControlProps>,
  (value: Range, inputError: InputError, index: number) => void,
  Record<string, string | false> | false,
] => {
  const lastValProp = useRef<Range | null>(null);
  const [value, setValue] = useState<Range>(() => {
    if (Array.isArray(valueProp) && valueProp.length === 2) {
      return [!isNull(valueProp[0]) ? valueProp[0] * 1 : null, !isNull(valueProp[1]) ? valueProp[1] * 1 : null];
    }
    return [null, null];
  });

  const [inputErrors, setInputErrors] = useState<(string | false)[]>([false, false]);
  useEffect(() => {
    const currVal: Range = Array.isArray(valueProp) && valueProp.length === 2 ? (valueProp as Range) : [null, null];
    if (lastValProp.current) {
      if (lastValProp.current[0] !== currVal[0] || lastValProp.current[1] !== currVal[1]) {
        lastValProp.current = [...currVal];
        setInputErrors([false, false]);
        setValue((curr) => {
          const next: Range = [!isNull(currVal[0]) ? (currVal[0] as number) * 1 : null, !isNull(currVal[1]) ? (currVal[1] as number) * 1 : null];
          if (curr[0] === next[0] && curr[1] === next[1]) {
            return curr;
          }
          return next;
        });
      }
    } else {
      lastValProp.current = [...currVal];
    }
  }, [valueProp]);

  const handleChangeMin = useEventCallback((min: number | null) => setValue((v) => [min, v[1]]));
  const handleChangeMax = useEventCallback((max: number | null) => setValue((v) => [v[0], max]));
  const controlProps = useMemo(() => {
    if (isObject(field.controls)) {
      const sharedProps: ControlProps = { ...(field.controls as ControlProps) };
      const min = sharedProps[minKey];
      const max = sharedProps[maxKey];
      delete sharedProps[minKey];
      delete sharedProps[maxKey];
      let minProps: ControlProps;
      let maxProps: ControlProps;
      if (isObject(min) && isObject(max)) {
        minProps = { onChangeValue: handleChangeMin, ...sharedProps, ...(min as ControlProps) };
        maxProps = { onChangeValue: handleChangeMax, ...sharedProps, ...(max as ControlProps) };
      } else if (isObject(min)) {
        minProps = { onChangeValue: handleChangeMin, ...sharedProps, [maxKey]: max, ...(min as ControlProps) };
        maxProps = { onChangeValue: handleChangeMax, ...sharedProps };
      } else if (isObject(max)) {
        minProps = { onChangeValue: handleChangeMin, ...sharedProps };
        maxProps = { onChangeValue: handleChangeMax, ...sharedProps, [minKey]: min, ...(max as ControlProps) };
      } else {
        minProps = { onChangeValue: handleChangeMin, ...(field.controls as ControlProps) };
        maxProps = { onChangeValue: handleChangeMax, ...(field.controls as ControlProps) };
      }
      return { [minKey]: minProps, [maxKey]: maxProps };
    }
    return { [minKey]: { onChangeValue: handleChangeMin }, [maxKey]: { onChangeValue: handleChangeMax } };
  }, [field, minKey, maxKey, handleChangeMax, handleChangeMin]);

  const [errors, setErrors] = useState<Record<string, string | false> | false>(false);
  const submit = useEventCallback((submitValue: Range, inputError: InputError, indexThatChanged: number) => {
    let next: Range = [...submitValue];

    let valid = true;
    const errorText: Record<string, string | false> = { [minKey]: false, [maxKey]: false };

    const nextInputErrors = [...inputErrors];
    if (indexThatChanged === 0 || indexThatChanged === 1) {
      nextInputErrors[indexThatChanged] = inputError && inputError.text ? inputError.text : false;
      setInputErrors(nextInputErrors);
    }
    const s1 = nextInputErrors[0] !== false ? nextInputErrors[0] : next[0];
    const s2 = nextInputErrors[1] !== false ? nextInputErrors[1] : next[1];
    for (const { test, name } of tests) {
      const minValid = test(s1, controlProps[minKey]);
      const maxValid = test(s2, controlProps[maxKey]);
      const message = (v: unknown, p: ControlProps | undefined) => {
        const entry = errorStrings[name];
        return typeof entry === 'function' ? entry(v, p ?? {}) : entry ?? 'error';
      };
      if (!minValid) {
        valid = false;
        errorText[minKey] = message(s1, controlProps[minKey]);
      }
      if (!maxValid) {
        valid = false;
        errorText[maxKey] = message(s2, controlProps[maxKey]);
      }
      if (!valid) {
        break;
      }
    }

    if (!valid) {
      setErrors(errorText);
    } else {
      if (nextInputErrors[0] || nextInputErrors[1]) {
        setInputErrors([false, false]);
      }
      setErrors(false);
    }

    if (valid && field.setField) {
      if (next[0] !== null && next[1] !== null) {
        if (next[0] > next[1]) {
          next = [next[indexThatChanged] as number, next[indexThatChanged] as number];
          setValue(next);
        }
      }
      if (lastValProp.current && next[0] === lastValProp.current[0] && next[1] === lastValProp.current[1]) {
        return;
      }
      field.setField(next, setQuery, field);
    }
  });
  return [value, setValue, controlProps, submit, errors];
};

const toNumber = (text: string): number | null => (text.trim() === '' ? null : Number(text));

function NumberBound({
  label,
  value,
  error,
  onChange,
  onCommit,
}: {
  label: string;
  value: number | null;
  error: string | false | undefined;
  onChange: (v: number | null) => void;
  onCommit: () => void;
}) {
  const id = useId();
  return (
    <div className="flex min-w-0 flex-1 flex-col gap-1.5">
      <Label htmlFor={id}>{label}</Label>
      <Input
        id={id}
        type="number"
        value={value === null ? '' : String(value)}
        aria-invalid={error ? true : undefined}
        onChange={(e) => onChange(toNumber(e.target.value))}
        onBlur={onCommit}
      />
      {error ? <p className="text-xs text-destructive">{error}</p> : null}
    </div>
  );
}

/** Two numbers, Min and Max. Today's version never wired its inputs (D26); this one does. */
export function MinMaxFilterControl({ field, value: valueProp, setQuery, active }: FilterControlProps) {
  const [value, setValue, , submit, errors] = useRangeControl(valueProp, field, setQuery, 'min', 'max', rangeTests, {
    number: () => `Not valid`,
    gteMin: (_v, p) => `Must be larger than ${p.min}`,
    lteMax: (_v, p) => `Must be smaller than ${p.max}`,
  });
  const latest = useRef(value);
  latest.current = value;
  return (
    <FilterGroup field={field} active={active}>
      <div className="flex w-full items-start gap-3">
        <NumberBound
          label="Min"
          value={value[0]}
          error={errors ? errors.min : false}
          onChange={(min) => {
            latest.current = [min, latest.current[1]];
            setValue(latest.current);
          }}
          onCommit={() => submit(latest.current, null, 0)}
        />
        <NumberBound
          label="Max"
          value={value[1]}
          error={errors ? errors.max : false}
          onChange={(max) => {
            latest.current = [latest.current[0], max];
            setValue(latest.current);
          }}
          onCommit={() => submit(latest.current, null, 1)}
        />
      </div>
    </FilterGroup>
  );
}

// DATES_FROM_TO field
export function DateRangeFilterControl({ field, value: valueProp, setQuery, active }: FilterControlProps) {
  const [value, setValue, , submit, errors] = useRangeControl(valueProp, field, setQuery, 'from', 'to', rangeTests, {
    number: () => `Not valid`,
    gteMin: (_v, p) => `Must be after ${p.from}`,
    lteMax: (_v, p) => `Must be before ${p.to}`,
  });
  const change = (index: 0 | 1) => (date: Date | null) => {
    const next: Range = [...value];
    next[index] = date ? date.getTime() : null;
    setValue(next);
    submit(next, null, index);
  };
  const error = errors ? errors.from || errors.to : false;
  return (
    <FilterGroup field={field} active={active}>
      <div className="flex w-full items-start gap-3">
        <div className="min-w-0 flex-1">
          <DateField label="From" clearable value={value[0] !== null ? new Date(value[0]) : null} onChange={change(0)} />
        </div>
        <div className="min-w-0 flex-1">
          <DateField label="To" clearable value={value[1] !== null ? new Date(value[1]) : null} onChange={change(1)} />
        </div>
      </div>
      {error ? <p className="text-xs text-destructive">{error}</p> : null}
    </FilterGroup>
  );
}
